import React, { useContext, useMemo, useState } from 'react';
import {
    Checkbox,
    List,
    ListItem,
    ListItemButton,
    ListItemIcon,
    ListItemText,
    Tooltip,
    useTheme,
} from '@mui/material';
import { reducedNodeIdToSelectionTree } from 'components/inputs/TreeTablePicker/helpers';
import { GetNodeIdT, reduceSelection } from 'helpers/treeHelpers';
import {
    EtablissementAndPayBudgetContext,
    OrgChartHrContext,
} from 'routes/HumanResourcesModule/components/contexts/HumanResourcesContext';
import Flex from 'ui/Flex/Flex';
import { SelectionTreeT } from 'business/definitions';
import {
    Etablissement,
    PayBudget,
    PayStateT,
} from 'routes/HumanResourcesModule/PayState/helpers/types';
import StyChip from 'routes/HumanResourcesModule/components/ui/StyChip';
import { useTranslation } from 'react-i18next';
import { HighlightPaper } from 'components/SharedComponents/HighlightContainer/HighlightPaper';
import TreeModalPicker from 'components/inputs/TreeTablePicker/TreeModalPicker';
import { enrichTreeWithTrueIsDisabledField } from 'routes/Admin/UserManagement/Users/CreateOrEditUser/components/ScopeFormStep/helpers/permission';
import InfoModal from 'components/SharedComponents/Modals/InfoModal';
import { HrSimulation } from 'routes/HumanResourcesModule/Simulation/helpers/types';
import { RetrospectiveT } from 'routes/HumanResourcesModule/Retrospective/helpers/types';

const OrgChartDetailsModal = ({
    isModalOpen,
    setIsModalOpen,
    orgChartHr,
    reducedSelection,
}: {
    isModalOpen: boolean;
    setIsModalOpen: React.Dispatch<React.SetStateAction<boolean>>;
    orgChartHr: TreeT;
    reducedSelection: SelectionTreeT[];
}) => {
    const tree = enrichTreeWithTrueIsDisabledField(orgChartHr);

    const { t } = useTranslation();
    return (
        <TreeModalPicker
            title={t('humanResources.orgChartHrsPerimeter')}
            show={isModalOpen}
            canEdit={false}
            onClose={() => setIsModalOpen(false)}
            tree={tree}
            scopeOptions={[]}
            defaultSelection={reducedSelection}
        />
    );
};

const EtablissementAndPayBudgetDetailsModal = ({
    field,
    isModalOpen,
    setIsModalOpen,
    items,
    isItemsFilterActive,
}: {
    field: 'etablissement' | 'pay_budget';
    isModalOpen: boolean;
    setIsModalOpen: React.Dispatch<React.SetStateAction<boolean>>;
    items: Etablissement[] | PayBudget[];
    isItemsFilterActive: boolean;
}) => {
    const { t } = useTranslation();
    const { payBudget, etablissement } = useContext(
        EtablissementAndPayBudgetContext,
    );
    const data: Etablissement[] | PayBudget[] =
        field === 'etablissement' ? etablissement : payBudget;
    return (
        <InfoModal
            isOpen={isItemsFilterActive && isModalOpen}
            onClose={() => setIsModalOpen(false)}
            title={t('humanResources.etablissementsPerimeter')}
        >
            <List
                sx={{
                    width: '100%',
                    maxWidth: 360,
                }}
            >
                {data.map((et) => {
                    const labelId = `checkbox-list-label-${et.name}`;

                    return (
                        <ListItem key={et.name} disablePadding>
                            <ListItemButton role={undefined} dense>
                                <ListItemIcon>
                                    <Checkbox
                                        edge="start"
                                        checked={items
                                            .map((item) => item.softwareId)
                                            .includes(et.softwareId)}
                                        tabIndex={-1}
                                        disableRipple
                                        inputProps={{
                                            'aria-labelledby': labelId,
                                        }}
                                        disabled
                                    />
                                </ListItemIcon>
                                <ListItemText id={labelId} primary={et.name} />
                            </ListItemButton>
                        </ListItem>
                    );
                })}
            </List>
        </InfoModal>
    );
};

type Props = {
    item: HrSimulation | PayStateT | RetrospectiveT;
    maxChipsToShow?: number;
};

/**
 * Display the services in `orgChartHrs` and the establishments in a banner, where each one is displayed as a Chip.
 * When there is more than 3 services or establishments, we display a Chip with the number of services not displayed.
 *
 * This component expect a filled `OrgChartHrContext`. It used it to reconstruct the service tree so that:
 * - When a parent and all of its child are selected, only the parent is shown
 * - When not all child are selected, then we display the selected parent and every selected child
 */
const EtablissementsPayBudgetsAndOrgChartChips = ({
    item,
    maxChipsToShow = 3,
}: Props) => {
    const {
        isEtablissementsFilterActive,
        etablissements,
        isPayBudgetFilterActive,
        payBudgets,
        orgChartHrs,
    } = item;
    const { t } = useTranslation();
    const orgChartHr = useContext(OrgChartHrContext);
    const [orgChartDetails, setOrgChartDetails] = useState(false);
    const [etablissementsDetails, setEtablissementsDetails] = useState(false);
    const [payBudgetsDetails, setPayBudgetsDetails] = useState(false);

    const reducedSelection = useMemo(() => {
        const getNodeId: GetNodeIdT = (node) => node.value;
        return reduceSelection(
            orgChartHr,
            orgChartHrs.map((och) => och.softwareId),
            getNodeId,
        ).map((el) => reducedNodeIdToSelectionTree(el, orgChartHr, getNodeId));
    }, [orgChartHr, orgChartHrs]);

    const theme = useTheme();

    const chips = useMemo(() => {
        const orgChartHrsChips = reducedSelection
            .slice(0, maxChipsToShow)
            .map((o) => (
                <Tooltip title={o.label} key={o.label}>
                    <StyChip
                        label={o.label}
                        styledColor={theme.palette.primary.main}
                        onClick={() => setOrgChartDetails(true)}
                        sx={{ maxWidth: '25ch' }}
                    />
                </Tooltip>
            ));

        if (reducedSelection.length > maxChipsToShow) {
            orgChartHrsChips.push(
                <StyChip
                    key="more-chips"
                    label={`+${reducedSelection.length - maxChipsToShow}`}
                    styledColor={theme.palette.primary.main}
                    onClick={() => setOrgChartDetails(true)}
                    sx={{ maxWidth: '25ch' }}
                />,
            );
        }

        const etablissementsChips = etablissements
            .slice(0, maxChipsToShow)
            .map((e) => (
                <Tooltip title={e.name} key={e.softwareId}>
                    <StyChip
                        label={e.name}
                        styledColor={theme.palette.primary.main}
                        onClick={() => setEtablissementsDetails(true)}
                        sx={{ maxWidth: '25ch' }}
                    />
                </Tooltip>
            ));

        if (etablissements.length > maxChipsToShow) {
            etablissementsChips.push(
                <StyChip
                    key="more-chips"
                    label={`+${etablissements.length - maxChipsToShow}`}
                    styledColor={theme.palette.primary.main}
                    onClick={() => setEtablissementsDetails(true)}
                    sx={{ maxWidth: '25ch' }}
                />,
            );
        }
        const payBudgetsChips = payBudgets.slice(0, maxChipsToShow).map((e) => (
            <Tooltip title={e.name} key={e.softwareId}>
                <StyChip
                    label={e.name}
                    styledColor={theme.palette.primary.main}
                    onClick={() => setPayBudgetsDetails(true)}
                    sx={{ maxWidth: '25ch' }}
                />
            </Tooltip>
        ));

        if (payBudgets.length > maxChipsToShow) {
            payBudgetsChips.push(
                <StyChip
                    key="more-chips"
                    label={`+${payBudgets.length - maxChipsToShow}`}
                    styledColor={theme.palette.primary.main}
                    onClick={() => setPayBudgetsDetails(true)}
                    sx={{ maxWidth: '25ch' }}
                />,
            );
        }

        return { orgChartHrsChips, etablissementsChips, payBudgetsChips };
    }, [
        reducedSelection,
        maxChipsToShow,
        theme.palette.primary.main,
        etablissements,
        payBudgets,
    ]);

    return (
        <Flex direction="row" gap={1}>
            <HighlightPaper
                title={t('humanResources.orgChartHrsPerimeter')}
                textColor="primary"
                fullWidth={true}
            >
                <Flex gap={1} alignItems="center" wrap="wrap">
                    {chips.orgChartHrsChips}
                </Flex>
                <OrgChartDetailsModal
                    isModalOpen={orgChartDetails}
                    setIsModalOpen={setOrgChartDetails}
                    orgChartHr={orgChartHr}
                    reducedSelection={reducedSelection}
                />
            </HighlightPaper>
            {isEtablissementsFilterActive && etablissements && (
                <HighlightPaper
                    title={t('humanResources.etablissementsPerimeter')}
                    textColor="primary"
                    fullWidth={true}
                >
                    <Flex gap={1} alignItems="center" wrap="wrap">
                        {chips.etablissementsChips}
                    </Flex>
                    <EtablissementAndPayBudgetDetailsModal
                        field="etablissement"
                        isModalOpen={etablissementsDetails}
                        setIsModalOpen={setEtablissementsDetails}
                        isItemsFilterActive={isEtablissementsFilterActive}
                        items={etablissements}
                    />
                </HighlightPaper>
            )}
            {isPayBudgetFilterActive && payBudgets && (
                <HighlightPaper
                    title={t('humanResources.payBudgetsPerimeter')}
                    textColor="primary"
                    fullWidth={true}
                >
                    <Flex gap={1} alignItems="center" wrap="wrap">
                        {chips.payBudgetsChips}
                    </Flex>
                    <EtablissementAndPayBudgetDetailsModal
                        field="pay_budget"
                        isModalOpen={payBudgetsDetails}
                        setIsModalOpen={setPayBudgetsDetails}
                        isItemsFilterActive={isPayBudgetFilterActive}
                        items={payBudgets}
                    />
                </HighlightPaper>
            )}
        </Flex>
    );
};

export default EtablissementsPayBudgetsAndOrgChartChips;
